// Geometry for the long-press corner fan, after the ActionNode bench block:
// buttons ride a path offset from the card's own outline (along the top
// edge, round the corner on an arc concentric with it, down the side), are
// placed by arc length, and fold back to a point provably hidden behind the
// card. Pure: no DOM, unit-tested.
//
// Coordinates are relative to the card's top-right corner, x to the right,
// y down. The menu mirrors them for the other corners.

export interface FanPoint {
  x: number;
  y: number;
  /** Direction out of the card at this point (unit vector). */
  nx: number;
  ny: number;
}

/** A point `s` along the offset path. `s` is arc length from where the corner arc meets the top edge. */
export function ride(s: number, corner: number, off: number): FanPoint {
  // Concentric with the card's corner; floored just above zero for a square card
  const arc = Math.max(0.001, corner + off);
  const len = (arc * Math.PI) / 2;
  if (s <= 0) return { x: -corner + s, y: -off, nx: 0, ny: -1 };
  if (s >= len) return { x: off, y: corner + (s - len), nx: 1, ny: 0 };
  const th = Math.PI / 2 - s / arc;
  return { x: -corner + arc * Math.cos(th), y: corner - arc * Math.sin(th), nx: Math.cos(th), ny: -Math.sin(th) };
}

/**
 * How far in along the corner's diagonal a button of radius `r` is wholly
 * behind the card. A rounded corner's own centre works when it is at least
 * r + 2 from every edge; a tighter corner has no room there, so go past its
 * disc to where both edges are clear.
 */
export function hideAt(corner: number, r: number): number {
  return corner >= r + 2 ? corner * Math.SQRT2 : Math.max(r * Math.SQRT2 + 2, corner * Math.SQRT2 + corner + 2);
}

/**
 * Centres for `n` buttons spread symmetrically about the corner's diagonal,
 * `spacing` apart along the path, `gap` from the card to each button's edge.
 */
export function fanSpots(n: number, corner: number, gap: number, r: number, spacing: number): FanPoint[] {
  const off = gap + r;
  const mid = ((corner + off) * Math.PI) / 4;
  return Array.from({ length: n }, (_, i) => ride(mid + (i - (n - 1) / 2) * spacing, corner, off));
}

/**
 * 0..100 into a spring's stiffness and per-frame decay, chosen by damping
 * ratio: 0 is heavy and arrives without a ring, 50 rebounds once, 100 twice.
 */
export function springOf(tune: number): { k: number; d: number } {
  return { k: 0.08 + (tune / 100) * 0.16, d: 0.62 + (tune / 100) * 0.2 };
}
